// Core packages

// Third party packages

// Custom packages

/**
 * Pure helpers of the session: which entry of `user.userRoles` the session acts with.
 *
 * @since 3.0.0
 */

/**
 * The part of an entry of `user.userRoles` the choice reads: the role and the company, each one
 * either populated (`{ _id }`, as `GET /auth/check` serves them) or a bare id.
 *
 * @since 3.0.0
 */
export interface RequestedRoleCandidate {
  role?: unknown;
  company?: unknown;
}

/**
 * Id of a reference, populated or not: `""` for no reference at all.
 *
 * @since 3.0.0
 *
 * @param value A populated document (`{ _id }`), a bare id, or nothing
 */
function referenceId(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (value && typeof value === "object") {
    const id = (value as { _id?: unknown })._id;
    return id === null || id === undefined ? "" : String(id);
  }
  return "";
}

/**
 * The entry of `user.userRoles` the session acts with, read off the two cookies every request also
 * sends to the back-end (`requestedRole` as `X-Requested-Role`, `requestedCompany` as
 * `X-Requested-Company`).
 *
 * The same role can be held in several companies, each entry with its own switches
 * (`generalPermission`), and the role id alone does not tell them apart: reading the first entry of
 * the role applied the switches — and the company — of another company than the one the requests
 * name. The choice is the back-end's (`selectUserRoleSwitchCard`, behind `hasEffectivePerms`), so the
 * actions a page offers are the ones the back-end lets through:
 *  - with a company: the entry of the role in that company, else the entry of the role without a
 *    company (admin, operator: one entry for every company);
 *  - without a company: the entry without a company, else the role's only entry.
 *
 * Where the back-end finds none — a company the user no longer holds the role in, two companies and
 * none named — this keeps what the session did before the rule, the FIRST entry of the role: the
 * back-end refuses the actions it guards, instead of the user being locked out of the whole platform
 * on a reload. The role's only entry is that first entry too.
 *
 * `undefined` only when the user holds no entry of that role at all.
 *
 * @since 3.0.0
 *
 * @param userRoles          `user.userRoles` as `GET /auth/check` serves them
 * @param requestedRoleId    The `requestedRole` cookie: a role id
 * @param requestedCompanyId The `requestedCompany` cookie: a company id, `""` when there is none
 */
export function findRequestedUserRole<T extends RequestedRoleCandidate>(
  userRoles: T[] | null | undefined,
  requestedRoleId: string,
  requestedCompanyId?: string
): T | undefined {
  if (!Array.isArray(userRoles) || !requestedRoleId) {
    return undefined;
  }
  const candidates = userRoles.filter(
    (entry) => !!entry && referenceId(entry.role) === requestedRoleId
  );
  if (!candidates.length) {
    return undefined;
  }

  if (requestedCompanyId) {
    const inCompany = candidates.find(
      (entry) => referenceId(entry.company) === requestedCompanyId
    );
    if (inCompany) {
      return inCompany;
    }
  }
  const companyless = candidates.find((entry) => referenceId(entry.company) === "");
  if (companyless) {
    return companyless;
  }
  return candidates[0];
}
